import { apiClient } from '@/core/api/client';
import type {
  Ticket,
  TicketComment,
  TicketHistory,
  TicketCategory,
  TicketStats,
  TicketUser,
  CreateTicketData,
  UpdateTicketData,
  TicketFilters,
  PaginatedResponse,
} from '../types/ticketing.types';

const BASE_URL = '/api/admin/bureau/ticketing';

// Note: apiClient interceptor already unwraps response.data, so we return the response directly

export const ticketingAPI = {
  // Stats
  async getStats(): Promise<{ success: boolean; data: TicketStats }> {
    return apiClient.get<{ success: boolean; data: TicketStats }>(`${BASE_URL}/stats`);
  },

  // Categories
  async getCategories(): Promise<{ success: boolean; data: TicketCategory[] }> {
    return apiClient.get<{ success: boolean; data: TicketCategory[] }>(`${BASE_URL}/categories`);
  },

  // Users for assignment
  async getUsers(): Promise<{ success: boolean; data: TicketUser[] }> {
    return apiClient.get<{ success: boolean; data: TicketUser[] }>(`${BASE_URL}/users`);
  },

  // Tickets
  async getTickets(filters?: TicketFilters): Promise<PaginatedResponse<Ticket>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    return apiClient.get<PaginatedResponse<Ticket>>(`${BASE_URL}/tickets?${params.toString()}`);
  },

  async getTicketById(id: string): Promise<{ success: boolean; data: Ticket }> {
    return apiClient.get<{ success: boolean; data: Ticket }>(`${BASE_URL}/tickets/${id}`);
  },

  async createTicket(data: CreateTicketData): Promise<{ success: boolean; data: Ticket; message: string }> {
    return apiClient.post<{ success: boolean; data: Ticket; message: string }>(`${BASE_URL}/tickets`, data);
  },

  async updateTicket(id: string, data: UpdateTicketData): Promise<{ success: boolean; data: Ticket; message: string }> {
    return apiClient.put<{ success: boolean; data: Ticket; message: string }>(`${BASE_URL}/tickets/${id}`, data);
  },

  async deleteTicket(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`${BASE_URL}/tickets/${id}`);
  },

  // Comments
  async getComments(ticketId: string): Promise<{ success: boolean; data: TicketComment[] }> {
    return apiClient.get<{ success: boolean; data: TicketComment[] }>(`${BASE_URL}/tickets/${ticketId}/comments`);
  },

  async addComment(
    ticketId: string,
    message: string,
    isInternal: boolean = false
  ): Promise<{ success: boolean; data: TicketComment; message: string }> {
    return apiClient.post<{ success: boolean; data: TicketComment; message: string }>(
      `${BASE_URL}/tickets/${ticketId}/comments`,
      { message, is_internal: isInternal }
    );
  },

  async deleteComment(commentId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`${BASE_URL}/comments/${commentId}`);
  },

  // Watch
  async toggleWatch(ticketId: string): Promise<{ success: boolean; watching: boolean; message: string }> {
    return apiClient.post<{ success: boolean; watching: boolean; message: string }>(`${BASE_URL}/tickets/${ticketId}/watch`);
  },

  // History
  async getHistory(ticketId: string): Promise<{ success: boolean; data: TicketHistory[] }> {
    return apiClient.get<{ success: boolean; data: TicketHistory[] }>(`${BASE_URL}/tickets/${ticketId}/history`);
  },

  // Mentions
  async getUnreadMentionCount(): Promise<{ success: boolean; data: { count: number } }> {
    return apiClient.get<{ success: boolean; data: { count: number } }>(`${BASE_URL}/mentions/unread/count`);
  },

  async getTicketsWithUnreadMentions(): Promise<{ success: boolean; data: Array<{ ticket_id: string; ticket_number: number; title: string; mention_count: number }> }> {
    return apiClient.get<{ success: boolean; data: Array<{ ticket_id: string; ticket_number: number; title: string; mention_count: number }> }>(`${BASE_URL}/mentions/unread/tickets`);
  },

  async markMentionsAsRead(ticketId: string): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(`${BASE_URL}/tickets/${ticketId}/mentions/mark-read`);
  },

  // Claude Processing
  async triggerClaudeAnalysis(ticketId: string): Promise<{ success: boolean; message: string; data?: { ticket_id: string; ticket_number: number; claude_status: string } }> {
    return apiClient.post<{ success: boolean; message: string; data?: { ticket_id: string; ticket_number: number; claude_status: string } }>(`${BASE_URL}/tickets/${ticketId}/claude-analyze`);
  },

  async getClaudeStatus(ticketId: string): Promise<{ success: boolean; data: { claude_status: string | null; prd_file_path: string | null; claude_processed_at: string | null; logs: Array<{ id: string; action: string; created_at: string; status: string }> } }> {
    return apiClient.get<{ success: boolean; data: { claude_status: string | null; prd_file_path: string | null; claude_processed_at: string | null; logs: Array<{ id: string; action: string; created_at: string; status: string }> } }>(`${BASE_URL}/tickets/${ticketId}/claude-status`);
  },

  async markImplementationDone(ticketId: string, conclusion: string): Promise<{ success: boolean; message: string; data?: { ticket_id: string; claude_status: string; tester_user_id: string } }> {
    return apiClient.post<{ success: boolean; message: string; data?: { ticket_id: string; claude_status: string; tester_user_id: string } }>(`${BASE_URL}/tickets/${ticketId}/implementation-done`, { conclusion });
  },

  async submitTestFeedback(ticketId: string, works: boolean, message?: string): Promise<{ success: boolean; message: string; data?: { ticket_id: string; status: string; claude_status: string } }> {
    return apiClient.post<{ success: boolean; message: string; data?: { ticket_id: string; status: string; claude_status: string } }>(`${BASE_URL}/tickets/${ticketId}/test-feedback`, { works, message });
  },
};
